import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { getProducts } from '../services/productService';

const Wishlist = () => {
  const navigate = useNavigate();
  const { formatMoney, getProductPrice } = useCurrency();
  const { wishlistIds: ids, removeFromWishlist } = useWishlist();

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const all = await getProducts();
        setProducts(Array.isArray(all) ? all : []);
      } catch {
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const wishlistProducts = useMemo(() => {
    const set = new Set((ids || []).map(String));
    return (products || []).filter((p) => set.has(String(p?.id)));
  }, [ids, products]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-gray-500">Loading wishlist...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-medium tracking-wide mb-2">WISHLIST</h1>
          <p className="text-sm text-gray-600">{wishlistProducts.length} items</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-sm underline hover:opacity-60"
        >
          Back
        </button>
      </div>

      {wishlistProducts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-500 mb-4">Your wishlist is empty.</p>
          <Link to="/shop/new-arrivals" className="text-sm underline hover:opacity-60">
            Browse new arrivals
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {wishlistProducts.map((product) => (
            <div key={product.id} className="group">
              <div className="aspect-square bg-gray-100 mb-3 overflow-hidden relative">
                <Link to={`/product/${product.id}`}>
                  <img
                    src={product.images?.[0]}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </Link>
                <button
                  onClick={() => {
                    removeFromWishlist(product.id);
                  }}
                  className="absolute top-2 right-2 bg-white/90 border border-gray-200 w-9 h-9 flex items-center justify-center hover:bg-white"
                  aria-label="Remove from wishlist"
                >
                  <X size={16} />
                </button>
                {!product.inStock && (
                  <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1">
                    SOLD OUT
                  </div>
                )}
              </div>
              <Link to={`/product/${product.id}`}>
                <h3 className="text-sm font-medium mb-1">{product.name}</h3>
                <p className="text-sm">{formatMoney(getProductPrice(product))}</p>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
